import React from 'react'
import { Box, Grid, generateUtilityClasses, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { CircularProgress } from '@mui/material'
import { styled } from '@mui/system'
import { useNavigate } from 'react-router-dom'
import { appRoute } from '../../../../../constants'
import { stableSort, getComparator } from '../../../../../utils/sorting'
import TableSortLabel from '@mui/material/TableSortLabel'
import { visuallyHidden } from '@mui/utils'
import { storageGet, storageSet } from '../../../../../utils/storage'
import { UspReportInventoryAgingResult } from '../../../../../models'
import { formatNumber } from '../../../../../utils/general'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'tableHead', 'tableCellName'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    margin: theme.spacing(3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },

  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
}))

interface Props {
  filterData: UspReportInventoryAgingResult[]
  isLoading: boolean
}

const InventoryAgingTableBlock = ({ filterData, isLoading }: Props) => {
  const navigate = useNavigate()
  const initialOrder = (storageGet('reports_inventory_aging_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('reports_inventory_aging_order_by') || 'StockNumber') as keyof UspReportInventoryAgingResult
  const [order, setOrder] = React.useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = React.useState<keyof UspReportInventoryAgingResult>(initialOrderBy)

  const handleEdit = (id: number | string) => {
    if (id) {
      navigate(appRoute.InventoryEdit.replace(':carID', String(id)))
    }
  }

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspReportInventoryAgingResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('reports_inventory_aging_order', isAsc ? 'desc' : 'asc')
    storageSet('reports_inventory_aging_order_by', property)
  }

  const createSortHandler = (property: keyof UspReportInventoryAgingResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }
  return (
    <TableContainer sx={{ maxHeight: 'calc(100vh - 118px)' }}>
      <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'StockNumber' ? order : false}>
              <TableSortLabel
                active={orderBy === 'StockNumber'}
                direction={orderBy === 'StockNumber' ? order : 'asc'}
                onClick={createSortHandler('StockNumber')}
              >
                Stock #
                {orderBy === 'StockNumber' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Vendor' ? order : false}>
              <TableSortLabel active={orderBy === 'Vendor'} direction={orderBy === 'Vendor' ? order : 'asc'} onClick={createSortHandler('Vendor')}>
                Vendor
                {orderBy === 'Vendor' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Branch' ? order : false}>
              <TableSortLabel active={orderBy === 'Branch'} direction={orderBy === 'Branch' ? order : 'asc'} onClick={createSortHandler('Branch')}>
                Branch
                {orderBy === 'Branch' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Status' ? order : false}>
              <TableSortLabel active={orderBy === 'Status'} direction={orderBy === 'Status' ? order : 'asc'} onClick={createSortHandler('Status')}>
                Status
                {orderBy === 'Status' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Year' ? order : false}>
              <TableSortLabel active={orderBy === 'Year'} direction={orderBy === 'Year' ? order : 'asc'} onClick={createSortHandler('Year')}>
                Year
                {orderBy === 'Year' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Make' ? order : false}>
              <TableSortLabel active={orderBy === 'Make'} direction={orderBy === 'Make' ? order : 'asc'} onClick={createSortHandler('Make')}>
                Make
                {orderBy === 'Make' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Model' ? order : false}>
              <TableSortLabel active={orderBy === 'Model'} direction={orderBy === 'Model' ? order : 'asc'} onClick={createSortHandler('Model')}>
                Model
                {orderBy === 'Model' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BodyType' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BodyType'}
                direction={orderBy === 'BodyType' ? order : 'asc'}
                onClick={createSortHandler('BodyType')}
              >
                Body
                {orderBy === 'BodyType' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'VIN' ? order : false}>
              <TableSortLabel active={orderBy === 'VIN'} direction={orderBy === 'VIN' ? order : 'asc'} onClick={createSortHandler('VIN')}>
                VIN
                {orderBy === 'VIN' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'DaysInInventory' ? order : false}>
              <TableSortLabel
                active={orderBy === 'DaysInInventory'}
                direction={orderBy === 'DaysInInventory' ? order : 'asc'}
                onClick={createSortHandler('DaysInInventory')}
              >
                Days
                {orderBy === 'DaysInInventory' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Category1' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Category1'}
                direction={orderBy === 'Category1' ? order : 'asc'}
                onClick={createSortHandler('Category1')}
              >
                1-30 Days
                {orderBy === 'Category1' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Category2' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Category2'}
                direction={orderBy === 'Category2' ? order : 'asc'}
                onClick={createSortHandler('Category2')}
              >
                31-60 Days
                {orderBy === 'Category2' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Category3' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Category3'}
                direction={orderBy === 'Category3' ? order : 'asc'}
                onClick={createSortHandler('Category3')}
              >
                61-90 Days
                {orderBy === 'Category3' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Category4' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Category4'}
                direction={orderBy === 'Category4' ? order : 'asc'}
                onClick={createSortHandler('Category4')}
              >
                91+ Days
                {orderBy === 'Category4' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'TotalCost' ? order : false}>
              <TableSortLabel
                active={orderBy === 'TotalCost'}
                direction={orderBy === 'TotalCost' ? order : 'asc'}
                onClick={createSortHandler('TotalCost')}
              >
                Total Cost
                {orderBy === 'TotalCost' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stableSort(getComparator(order, orderBy), filterData as any[]).map((item, index) => (
            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell className={classes.tableCellName} onClick={() => handleEdit(item.CarID as string)}>
                {item.StockNumber}
              </TableCell>
              <TableCell>{item?.Vendor}</TableCell>
              <TableCell>{item?.Branch}</TableCell>
              <TableCell>{item?.Status}</TableCell>
              <TableCell>{item?.Year}</TableCell>
              <TableCell>{item?.Make}</TableCell>
              <TableCell>{item?.Model}</TableCell>
              <TableCell>{item?.BodyType}</TableCell>
              <TableCell>{item?.VIN}</TableCell>
              <TableCell>{item?.DaysInInventory}</TableCell>
              <TableCell>{formatNumber(item?.Category1 as string)}</TableCell>
              <TableCell>{formatNumber(item?.Category2 as string)}</TableCell>
              <TableCell>{formatNumber(item?.Category3 as string)}</TableCell>
              <TableCell>{formatNumber(item?.Category4 as string)}</TableCell>
              <TableCell>{formatNumber(item?.TotalCost as string)}</TableCell>
            </TableRow>
          ))}
          {filterData?.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align='center'>
                No results
              </TableCell>
            </TableRow>
          )}
          {isLoading && (
            <TableRow>
              <TableCell colSpan={10} align='center'>
                <CircularProgress />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default InventoryAgingTableBlock
